package music.jammers.client.shared.event.presentation.viewmodel

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.consumeAsFlow
import music.jammers.client.shared.analytics.event.EventAnalytics
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.component.customExceptionHandler
import music.jammers.client.shared.component.track.presentation.model.TrackCardModel
import music.jammers.client.shared.component.update
import music.jammers.client.shared.event.domain.usecase.GetUserAcceptanceUseCase
import music.jammers.client.shared.event.track.domain.usecase.SignInTrackUseCase
import music.jammers.client.shared.event.track.domain.usecase.SignOutTrackUseCase
import music.jammers.shared.event.domain.model.EventMember
import music.jammers.shared.track.domain.model.Track
import music.jammers.shared.user.domain.model.User
import music.limasoul.client.shared.event.domain.usecase.GetEventDetailsUseCase
import music.limasoul.client.shared.navigation.Navigation
import music.limasoul.client.shared.navigation.Router
import music.limasoul.client.shared.user.domain.usecase.GetCurrentUserUseCase

class EventDetailsViewModel(
    private val getEventDetailsUseCase: GetEventDetailsUseCase,
    private val signInTrackUseCase: SignInTrackUseCase,
    private val signOutTrackUseCase: SignOutTrackUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getUserAcceptanceUseCase: GetUserAcceptanceUseCase,
    private val eventAnalytics: EventAnalytics,
    private val router: Router,
    initState: EventDetailsState = EventDetailsState(),
) : BaseViewModel() {

    private val _state: MutableState<EventDetailsState> = mutableStateOf(initState)
    internal val state: State<EventDetailsState> get() = _state

    internal val snackBarState: Flow<Unit> by lazy {
        _snackBarState.consumeAsFlow()
    }

    private val _snackBarState = Channel<Unit>()

    internal fun onBind(eventId: Long) {
        _state.update {
            copy(
                eventId = eventId,
                progress = true
            )
        }

        launch(errorHandler) {
            loadEventDetails()
        }
    }

    private val errorHandler = customExceptionHandler(router) { _, throwable ->
        eventAnalytics.onError(throwable)

        _state.update {
            copy(
                progress = false,
                error = throwable,
            )
        }
    }

    private suspend fun loadEventDetails() {
        val eventDetails = getEventDetailsUseCase.invoke(state.value.eventId)
        val acceptance = getUserAcceptanceUseCase(state.value.eventId)
        val user = getCurrentUserUseCase()

        val tracks: MutableList<EventDetailsItem> = eventDetails.tracks.map { item ->
            EventDetailsItem.Item(
                TrackCardModel(
                    eventTrack = item,
                    availableMembers = getAvailableMembers(item.members),
                    isMyTrack = isMyTrack(user, item.members),
                )
            )
        }.toMutableList()

        if (eventDetails.backlog.isNotEmpty()) {
            val backlog = eventDetails.backlog.map { item ->
                EventDetailsItem.Item(
                    TrackCardModel(
                        eventTrack = item,
                        availableMembers = getAvailableMembers(item.members),
                        isMyTrack(user, item.members),
                    )
                )
            }

            tracks.add(EventDetailsItem.Header)
            tracks.addAll(backlog)
        }

        _state.update {
            copy(
                title = eventDetails.title,
                date = eventDetails.date,
                spot = eventDetails.spot,
                progress = false,
                tracks = tracks,
                user = user,
                frozen = eventDetails.frozen,
                userAcceptance = acceptance,
            )
        }
    }

    private fun getAvailableMembers(allMembers: List<EventMember>): List<EventMember> =
        allMembers.filter { item ->
            item.status != EventMember.Status.BOOKED
        }

    private fun isMyTrack(user: User?, eventMembers: List<EventMember>): Boolean =
        user != null && !eventMembers.none { item ->
            item.user == user
        }

    fun onErrorShown() {
        _state.update {
            copy(
                error = null
            )
        }
    }

    internal fun onBackPressed() {
        router.back()
    }

    internal fun onEditClicked() {
        router.navigateTo(Navigation.Event.Edit(state.value.eventId))
    }

    internal fun onTrackClicked(eventTrackId: Long) {
        router.navigateTo(Navigation.EventTrack.Details(state.value.eventId, eventTrackId))
    }

    internal fun onMemberClicked(track: Track, eventMember: EventMember) {
        if (isCurrentUserMember(eventMember)) {
            eventAnalytics.onCancelMember(eventMember.id)
            _state.update {
                copy(
                    confirmSignOutTrack = track to eventMember
                )
            }
        } else if (eventMember.isAvailable) {
            eventAnalytics.onAcceptMember(eventMember.id)
            _state.update {
                copy(
                    confirmSignInTrack = track to eventMember,
                )
            }
        }
    }

    private fun isCurrentUserMember(eventMember: EventMember): Boolean =
        state.value.user?.let { currentUser ->
            currentUser == eventMember.user
        } ?: false

    internal fun onTrackAddClicked() {
        router.navigateTo(Navigation.EventTrack.Create(state.value.eventId))
    }

    internal fun onSignInPositive(eventMember: EventMember) {
        eventAnalytics.onSignInSuccess(eventMember.id)

        _state.update {
            copy(
                progress = true
            )
        }

        launch(errorHandler) {
            val member = signInTrackUseCase(eventMember)

            if (member.status == EventMember.Status.BOOKED_OPTIONAL) {
                _snackBarState.send(Unit)
            }

            _state.update {
                copy(
                    progress = false,
                    confirmSignInTrack = null,
                )
            }

            loadEventDetails()
        }
    }

    internal fun onDialogNegative() {
        _state.update {
            copy(
                confirmSignInTrack = null,
                confirmSignOutTrack = null,
            )
        }
    }

    internal fun onSignOutPositive(eventMember: EventMember) {
        eventAnalytics.onSignOutSuccess(eventMember.id)

        _state.update {
            copy(
                progress = true
            )
        }

        launch(errorHandler) {
            signOutTrackUseCase(eventMember)

            _state.update {
                copy(
                    confirmSignOutTrack = null
                )
            }

            loadEventDetails()
        }
    }

    internal fun onCommitTrackListClicked() {
        router.navigateTo(Navigation.Event.Freeze(state.value.eventId))
    }
}